import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { KakaotalkTemplatesService } from '../../kakaotalk-templates.service';
import { KakaotalkTemplateButtonTypeEnum, KakaotalkTemplateForm, KakaotalkTemplateItem } from '../../shared';
import { UrlValidator } from './shared/button-url-example.validator';

@Component({
  selector: 'app-kakaotalk-template-languages',
  templateUrl: './kakaotalk-template-languages.component.html',
  styleUrls: ['./kakaotalk-template-languages.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class KakaotalkTemplateLanguagesComponent implements OnInit, OnDestroy {
  // {code, template} code means the language code related to the tab, template is the template regarding the selected language.
  @Input() form: FormGroup;
  @Input() template: KakaotalkTemplateForm;

  buttonsGrouped = new FormGroup({
    url: new FormArray([]),
  });

  get buttons() {
    return this.form?.get('template.buttons') as FormArray;
  }

  get urlButtons() {
    return this.buttonsGrouped?.get('url') as FormArray;
  }

  get body() {
    return this.form?.get('template.body') as FormControl;
  }

  get placeholders() {
    return this.form?.get('template.placeholders') as FormGroup;
  }

  KakaotalkTemplateButtonTypeEnum = KakaotalkTemplateButtonTypeEnum;

  templates: KakaotalkTemplateItem[];
  cannotAddButton: boolean;
  buttonsTypesSet = new Set();

  placeholdersControls: string[] = [];
  urlPlaceholders = {};
  limitations: {
    maxLength: any;
    maxNumber: any;
  };

  userLanguage = this.alcmeonContextService.lang;
  /**
   * Especially used for the buttons grouped by type and the drag and drop mechanism.
   */
  sections = [
    {
      type: KakaotalkTemplateButtonTypeEnum.URL,
      formArray: this.urlButtons,
      titleKey: 'parameters/kakaotalk-templates/content/buttons/add-url',
      titleIcon: 'open_in_new',
    },
  ];
  private subscriptions = new Subscription();

  constructor(
    private kakaotalkTemplatesService: KakaotalkTemplatesService,
    private alcmeonContextService: AlcmeonContextService
  ) {
    this.limitations = this.kakaotalkTemplatesService.getLimitations();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.buttonsGrouped.valueChanges.pipe(debounceTime(100)).subscribe(() => {
        const { url } = this.buttonsGrouped.getRawValue(); // getRawValue instead of value to avoid undefined result (form disabled in readonly)
        const buttons = [].concat(url);

        this.cannotAddButton = this.buttonsGrouped.invalid || buttons.length > this.limitations.maxNumber.button - 1;

        this.computeButtonsFromButtonsGroupedTyped();
      })
    );

    const { template, code } = this.form.getRawValue();
    this.managePlaceholders(template.body);
    this.subscriptions.add(
      this.body.valueChanges.pipe(debounceTime(300)).subscribe(value => this.managePlaceholders(value))
    );

    if (this.template) {
      const templateResponse = (this.template.templates || []).find(t => t.code === code)?.template;

      if (templateResponse) {
        this.reinstantiateButtonsGroupedByType(templateResponse);
        this.form.get('template').patchValue(templateResponse);
      }
    } else {
      // we are displaying / switching between tabs
      this.reinstantiateButtonsGroupedByType(this.form.value.template);
    }
  }

  private reinstantiateButtonsGroupedByType(templateResponse) {
    // Manage sections order
    const buttonsTypesSet = templateResponse.buttons.reduce((result, button) => {
      result.add(button.type);
      return result;
    }, new Set());
    this.buttonsTypesSet = new Set(buttonsTypesSet);

    // Add missing types in correct order.
    this.sections.forEach(section => buttonsTypesSet.add(section.type));
    // Apply the order
    const buttonsTypesOrder = [...buttonsTypesSet];
    this.sections.sort((a, b) => (buttonsTypesOrder.indexOf(b.type) > buttonsTypesOrder.indexOf(a.type) ? -1 : 1));

    templateResponse.buttons.forEach(button => {
      switch (button.type) {
        case KakaotalkTemplateButtonTypeEnum.URL:
          this.addUrlButton(button);
          break;
        default:
          break;
      }
    });
  }

  private computeButtonsFromButtonsGroupedTyped() {
    // Mechanism to force update of validity.
    this.buttons.clear();
    this.sections.forEach(({ formArray }) =>
      (formArray.controls || []).forEach((formGroup: FormGroup) => {
        const value = formGroup.getRawValue();
        const { type } = value;
        let controlClone;
        switch (type) {
          case KakaotalkTemplateButtonTypeEnum.URL:
            controlClone = this.newUrlButtonFormGroup(value);
            break;
          default:
            break;
        }
        controlClone.patchValue(formGroup.getRawValue());
        this.buttons.push(controlClone);
      })
    );
  }

  private managePlaceholders(value: string) {
    /**
     * Contains the markers related to a placeholder.
     * @example
     * ['{{1}}', '{{2}}']
     */
    const placeholders: string[] = value?.match(new RegExp('{{[0-9]+}}', 'g')) || [];
    placeholders.forEach(this.updatePlaceholders);

    if (placeholders.length > this.limitations.maxNumber.placeholders) {
      this.body.setErrors({ tooManyPlaceholders: true }, { emitEvent: false });
    }
    Object.keys(this.placeholders.controls).forEach((controlName: string) => {
      if (!placeholders.includes(controlName)) {
        this.clearPlaceholder(controlName);
      }
    });
    this.placeholdersControls = Object.keys(this.placeholders.controls);
  }

  private updatePlaceholders = (label: string) => {
    if (!this.placeholders.get(label)) {
      this.placeholders.addControl(
        label,
        new FormGroup({
          label: new FormControl(null, Validators.required),
        })
      );
    }
  };

  private clearPlaceholder(controlName: string) {
    this.placeholders.removeControl(controlName);
  }

  addUrlButton(button?) {
    this.urlButtons.push(this.newUrlButtonFormGroup(button));
  }

  private newUrlButtonFormGroup(value): FormGroup {
    const group = new FormGroup({
      type: new FormControl(KakaotalkTemplateButtonTypeEnum.URL),
      text: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.button)]),
      url: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.url)]),
      example: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.example)]),
    });

    group.addValidators(UrlValidator.buttonUrlExampleValidator(this.limitations.maxLength.url));

    this.subscriptions.add(
      group.valueChanges.subscribe(({ url }) => {
        if (url && url.indexOf('{{1}}') > -1) {
          this.urlPlaceholders[url] = true;
          if (this.form.enabled) {
            group.controls.example.enable({ emitEvent: false });
          }
        } else {
          this.urlPlaceholders[url] = false;
          if (this.form.enabled) {
            group.controls.example.setValue(null, { emitEvent: false });
            group.controls.example.disable({ emitEvent: false });
            group.removeValidators(UrlValidator.buttonUrlExampleValidator(this.limitations.maxLength.url));
          }
        }
        const buttonUrlInvalid = group.hasError('button-url');
        const buttonUrlMaxLengthInvalid =
          group.hasError('button-url-max-length') || group.get('url').hasError('max-length');
        const buttonUrlFormatInvalid =
          !/^(https?:\/\/.)[-a-zA-Z0-9@:%._\\+~#=]{0,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/.test(
            group.value.url
          );
        group.get('url').setErrors(
          buttonUrlFormatInvalid || buttonUrlInvalid || buttonUrlMaxLengthInvalid
            ? {
                'button-url-max-length': buttonUrlMaxLengthInvalid,
                'button-url-format': buttonUrlFormatInvalid,
                'button-url': buttonUrlInvalid,
              }
            : null
        );
      })
    );

    if (value) {
      group.patchValue(value);
    }
    setTimeout(() => group.valueChanges.subscribe(() => this.form.markAsDirty()), 500);
    return group;
  }

  getUrlPreview(urlGroup: FormGroup) {
    const { url, example } = urlGroup.value;
    if (url && example) {
      const urlExample = url.replace(/(\{\{1\}\})/gi, example);
      try {
        const result = new URL(urlExample);
        return result.href;
      } catch {
        return '';
      }
    }
  }

  deleteButton(id: number, type: KakaotalkTemplateButtonTypeEnum) {
    switch (type) {
      case KakaotalkTemplateButtonTypeEnum.URL:
        this.urlButtons.removeAt(id);
        break;
    }
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.sections, event.previousIndex, event.currentIndex);
    this.computeButtonsFromButtonsGroupedTyped();
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
